import PlanGroupToggle from 'aa/vue/components/PlanGroupToggle';
import { tracking } from 'services/Tracking';

// placeholder event name until the tracking team registers the plan-group toggle
export const PLAN_GROUP_TOGGLE_ACTION = 'trackPlanGroupToggle';

/** The plan group of a picker plan (`planGroup`) or of a switch-page product (`plan_group`). */
export const planGroupOf = (plan) => plan?.planGroup || plan?.plan_group || '';

/**
 * Plan-group toggle shared by the plan picker and the switch page: plain plans on one side, the
 * plans of a group (a product tag listed in the regional planGroupTags) on the other. The host
 * component provides `planGroupPlans` (the plans of the displayed cadence), `planGroupCopy(key)`
 * (a page attribute lookup) and `planGroupStorageKey` (its own sessionStorage key, so each page
 * keeps its own choice). `defaultPlanGroup()` picks the side before the user has chosen one.
 * Without the toggle copy in the CMS the toggle stays hidden and the page shows the plain plans.
 * ponytail: one toggle = one group; a market with several groups needs a segmented control
 */
export default {
    components: { PlanGroupToggle },

    data() {
        return {
            // null = no choice stored yet; '' = plain plans; otherwise the chosen group
            planGroupChoice: null,
        };
    },

    created() {
        this.planGroupChoice = sessionStorage.getItem(this.planGroupStorageKey);
    },

    computed: {
        /**
         * The chosen or default group, or '' when the page has no toggle or no plan carries that
         * group: a stale stored value must not empty the page.
         */
        activePlanGroup() {
            const group = this.planGroupChoice ?? this.defaultPlanGroup();
            const onPage = this.planGroupPlans.some((plan) => planGroupOf(plan) === group);
            return this.planGroupToggle && onPage ? group : '';
        },

        planGroupToggle() {
            const group = this.planGroupPlans.map(planGroupOf).find(Boolean);
            if (!group) {
                return null;
            }
            const key = group.toLowerCase();
            const label = this.planGroupCopy(`aa_pap_plan_group_toggle_label_${key}`);
            const left = this.planGroupCopy(`aa_pap_plan_group_toggle_left_${key}`);
            const right = this.planGroupCopy(`aa_pap_plan_group_toggle_right_${key}`);
            if (!label || !left || !right) {
                return null;
            }
            return { group, label, left, right };
        },

        planGroupToggleSide() {
            const toggle = this.planGroupToggle;
            return this.activePlanGroup === toggle?.group ? toggle?.right : toggle?.left;
        },
    },

    methods: {
        /** the side shown before the user has chosen one; hosts override it */
        defaultPlanGroup() {
            return '';
        },

        onPlanGroupToggle(event) {
            const toggle = this.planGroupToggle;
            this.planGroupChoice = event.value === toggle.right ? toggle.group : '';
            sessionStorage.setItem(this.planGroupStorageKey, this.planGroupChoice);
            (this.$trackingService ?? tracking).trackAction(PLAN_GROUP_TOGGLE_ACTION, {
                planGroup: this.planGroupChoice || 'none',
            });
        },

        matchesPlanGroup(plan) {
            return planGroupOf(plan) === this.activePlanGroup;
        },
    },
};
